import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Settlement, PaymentMethod } from "@/types/payment";
import { useTranslation } from "react-i18next";
import { CreditCard, Building, Copy, Check, AlertCircle, Star } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
interface PaymentMethodDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedSettlement: Settlement | null;
  paymentMethod: PaymentMethod | null;
  onConfirmPayment: (settlement: Settlement) => void;
}
export function PaymentMethodDialog({
  open,
  onOpenChange,
  selectedSettlement,
  paymentMethod,
  onConfirmPayment
}: PaymentMethodDialogProps) {
  const {
    t
  } = useTranslation();
  const {
    toast
  } = useToast();
  const [copied, setCopied] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("paypay");

  // Set the initial tab based on preferred payment method or available methods
  useEffect(() => {
    if (paymentMethod) {
      if (paymentMethod.is_preferred) {
        // Use the preferred method's tab
        if (paymentMethod.paypay_username) {
          setActiveTab("paypay");
        } else if (hasBankInfo) {
          setActiveTab("bank");
        }
      } else {
        // Use the first available method
        if (paymentMethod.paypay_username) {
          setActiveTab("paypay");
        } else if (hasBankInfo) {
          setActiveTab("bank");
        }
      }
    }
  }, [paymentMethod, open]);
  useEffect(() => {
    // For debugging - log payment method to check if is_preferred flag is present
    if (paymentMethod) {
      console.log("Payment method in dialog:", paymentMethod);
      console.log("Is preferred:", paymentMethod.is_preferred);
    }
  }, [paymentMethod]);
  const handleCopy = (text: string, field: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(field);
      setTimeout(() => setCopied(null), 2000);
      toast({
        description: t('payments.copied'),
        className: "bg-green-500 text-white"
      });
    });
  };
  const hasBankInfo = paymentMethod?.bank_name || paymentMethod?.branch_name || paymentMethod?.account_number || paymentMethod?.account_holder_name;
  const hasPayPayInfo = paymentMethod?.paypay_username;
  const initialTab = paymentMethod?.is_preferred ? paymentMethod.paypay_username ? "paypay" : hasBankInfo ? "bank" : "paypay" : hasPayPayInfo ? "paypay" : hasBankInfo ? "bank" : "paypay";

  // Determine which payment method is preferred
  const preferredMethod = paymentMethod?.is_preferred ? paymentMethod.paypay_username ? "paypay" : "bank" : null;
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('payments.paymentDetails')}</DialogTitle>
          <DialogDescription className="text-xs">
            {t('payments.paymentInstructions')}
          </DialogDescription>
        </DialogHeader>

        {!paymentMethod ? <div className="flex flex-col items-center justify-center py-6 text-center">
            <AlertCircle className="h-10 w-10 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              {t('payments.noPaymentMethodsFound')}
            </p>
          </div> : <Tabs defaultValue={initialTab} className="w-full" onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="paypay" disabled={!hasPayPayInfo}>
                <CreditCard className="mr-2 h-4 w-4" />
                PayPay
              </TabsTrigger>
              <TabsTrigger value="bank" disabled={!hasBankInfo}>
                <Building className="mr-2 h-4 w-4" />
                {t('payments.bank')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="paypay" className="space-y-4 mt-4">
              {paymentMethod.paypay_username ? <div className="flex flex-col items-center justify-center space-y-4">
                  {/* Show preferred badge only if PayPay is the preferred method */}
                  {paymentMethod.is_preferred && preferredMethod === "paypay" && <div className="mb-2 flex items-center justify-center bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded-md border border-yellow-200 dark:border-yellow-800 w-full">
                      <Star className="h-4 w-4 text-yellow-500 mr-2 fill-yellow-400" />
                      <p className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
                        {t('payments.preferredMethodNotice', "This is the user's preferred payment method")}
                      </p>
                    </div>}
                  
                  <Avatar className="h-24 w-24 rounded-full overflow-hidden">
                    <AvatarImage src="https://tkrrgpbntaqiupgcayjm.supabase.co/storage/v1/object/sign/Images/PayPay.webp?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJJbWFnZXMvUGF5UGF5LndlYnAiLCJpYXQiOjE3NDE1MTA4MTEsImV4cCI6NDg5NTExMDgxMX0.jLjQWyxiefwGRyi70S_QSzUe4yGi63wVgpL5N-913ks" alt="PayPay Logo" className="object-cover" />
                    <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                      PP
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <p className="text-sm font-medium">{t('payments.paypayUsername')}</p>
                    </div>
                    <div className="flex items-center justify-center space-x-2 mt-1">
                      <p className="text-lg font-semibold">{paymentMethod.paypay_username}</p>
                      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => handleCopy(paymentMethod.paypay_username!, 'paypay')}>
                        {copied === 'paypay' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div> : <div className="flex flex-col items-center justify-center py-6 text-center">
                  <AlertCircle className="h-10 w-10 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {t('payments.noPayPayInfoFound')}
                  </p>
                </div>}
            </TabsContent>

            <TabsContent value="bank" className="space-y-4 mt-4">
              {hasBankInfo ? <div className="space-y-4">
                  {/* Show preferred badge only if Bank is the preferred method */}
                  {paymentMethod.is_preferred && preferredMethod === "bank" && <div className="mb-2 flex items-center justify-center bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded-md border border-yellow-200 dark:border-yellow-800">
                      <Star className="h-4 w-4 text-yellow-500 mr-2 fill-yellow-400" />
                      <p className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
                        {t('payments.preferredMethodNotice', "This is the user's preferred payment method")}
                      </p>
                    </div>}
                
                  {paymentMethod.bank_name && <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium">{t('payments.bankName')}</p>
                        <p className="text-sm">{paymentMethod.bank_name}</p>
                      </div>
                      <Button variant="outline" size="icon" onClick={() => handleCopy(paymentMethod.bank_name!, 'bank_name')}>
                        {copied === 'bank_name' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>}

                  {paymentMethod.branch_name && <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium">{t('auth.branchName')}</p>
                        <p className="text-sm">{paymentMethod.branch_name}</p>
                      </div>
                      <Button variant="outline" size="icon" onClick={() => handleCopy(paymentMethod.branch_name!, 'branch_name')}>
                        {copied === 'branch_name' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>}

                  {paymentMethod.account_type && <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium">{t('payments.accountType')}</p>
                        <p className="text-sm">{paymentMethod.account_type}</p>
                      </div>
                      <Button variant="outline" size="icon" onClick={() => handleCopy(paymentMethod.account_type!, 'account_type')}>
                        {copied === 'account_type' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>}

                  {paymentMethod.account_number && <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium">{t('payments.accountNumber')}</p>
                        <p className="text-sm">{paymentMethod.account_number}</p>
                      </div>
                      <Button variant="outline" size="icon" onClick={() => handleCopy(paymentMethod.account_number!, 'account_number')}>
                        {copied === 'account_number' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>}

                  {paymentMethod.account_holder_name && <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium">{t('payments.accountHolder')}</p>
                        <p className="text-sm">{paymentMethod.account_holder_name}</p>
                      </div>
                      <Button variant="outline" size="icon" onClick={() => handleCopy(paymentMethod.account_holder_name!, 'account_holder_name')}>
                        {copied === 'account_holder_name' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>}
                </div> : <div className="flex flex-col items-center justify-center py-6 text-center">
                  <AlertCircle className="h-10 w-10 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {t('payments.noBankInfoFound')}
                  </p>
                </div>}
            </TabsContent>
          </Tabs>}

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="sm:flex-1">
            {t('common.close')}
          </Button>
          <Button onClick={() => selectedSettlement && onConfirmPayment(selectedSettlement)} className="sm:flex-1">
            {t('payments.confirmPayment')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}